import * as Sentry from '@sentry/gatsby';
import { ApolloClient, InMemoryCache, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { createUploadLink } from 'apollo-upload-client';
import fetch from 'cross-fetch';
import jwtDecode from 'jwt-decode';
import { MIDDLEWARE_GRAPHQL_ENDPOINT, HPID_LOGOUT_URL } from '../settings';

let refreshing = false;
const REFRESH_TOKEN_LEGROOM = 5 * 60;
const REFRESH_ACCESS_QUERY = `
  query GetAccessToken($input: AccessTokenInput!) {
    accessToken(input: $input) {
      accessToken
    }
  }
`;

const enum Stores {
  GATSBY = 'hp-xy-gatsby',
  ID = 'hp-xy-id',
}

interface RefreshAccessData {
  accessToken: {
    accessToken: string;
  };
  errors?: {
    message: string;
  }[];
}

export function getTokenState(token?: string | null) {
  if (!token) {
    return {
      valid: false,
      needRefresh: true,
    };
  }

  const decoded: { exp: number } = jwtDecode(token);

  if (!decoded) {
    return {
      valid: false,
      needRefresh: true,
    };
  }

  const expirationDate = new Date(decoded.exp * 1000);
  const currentDate = new Date(Date.now() + REFRESH_TOKEN_LEGROOM);

  if (currentDate > expirationDate) {
    return {
      valid: true,
      needRefresh: true,
    };
  }

  return {
    valid: true,
    needRefresh: false,
  };
}

const refreshAuthToken = async () => {
  const tokenString =
    typeof Storage !== 'undefined' ? localStorage?.getItem(Stores.ID) : null;
  const { hpid, refresh } = JSON.parse(tokenString ?? '{}'); // JSON.parse('') throws an error

  const variables = {
    input: {
      refreshToken: refresh,
    },
  };

  const accessToken = await fetch(MIDDLEWARE_GRAPHQL_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: REFRESH_ACCESS_QUERY,
      variables,
    }),
  })
    .then((res) => res.json())
    .then(({ data }: { data: RefreshAccessData }) => {
      const newAccessToken = data?.accessToken?.accessToken;

      if (!newAccessToken) {
        throw new Error(
          `Failed to request new access token: ${JSON.stringify(data.errors)}.`,
        );
      }

      if (typeof Storage !== 'undefined') {
        localStorage?.setItem(
          Stores.ID,
          JSON.stringify({
            hpid,
            token: newAccessToken,
            refresh,
          }),
        );
      }

      return newAccessToken;
    })
    .catch((error) => {
      Sentry.captureException(error);
      const globalStateString =
        typeof Storage !== 'undefined'
          ? localStorage?.getItem(Stores.GATSBY)
          : null;
      const globalState = JSON.parse(globalStateString ?? '{}'); // JSON.parse('') throws an error

      if (typeof Storage !== 'undefined') {
        localStorage?.removeItem(Stores.ID);
        localStorage?.setItem(Stores.GATSBY, {
          ...globalState,
          purchased: [],
          user: undefined,
        });
      }

      if (typeof window !== 'undefined') {
        window.location.href = HPID_LOGOUT_URL;
      }
    });

  return accessToken;
};

export const setTimeoutPromise = async (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const apolloAuthLink = setContext(async (request, { headers }) => {
  let tokenString = localStorage?.getItem(Stores.ID);
  const tokenData = JSON.parse(tokenString ?? '{}'); // JSON.parse('') throws an error
  let { token } = tokenData;
  const { refresh } = tokenData;
  const tokenState = getTokenState(token);

  if (refresh && (tokenState.needRefresh || !tokenState.valid)) {
    if (refreshing) {
      while (refreshing) {
        /* eslint-disable no-await-in-loop */
        await setTimeoutPromise(1000);
      }
      tokenString = localStorage.getItem(Stores.ID);
      ({ token } = JSON.parse(tokenString ?? '{}'));
    } else {
      refreshing = true;
      token = await refreshAuthToken();
      refreshing = false;
    }
  }

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : null,
    },
  };
});

const uploadLink = createUploadLink({
  uri: MIDDLEWARE_GRAPHQL_ENDPOINT,
  fetch,
});

const middlewareClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: from([apolloAuthLink, uploadLink]),
});

export default middlewareClient;
